import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Textarea,
  Checkbox,
  CheckboxGroup,
} from "@chakra-ui/react";
import CDS from "design-system";

import { PREVIEW_PLACEHOLDER, PREVIEW_PLACEHOLDER_WIDTH } from "../constants";
import type { CustomFormField, CustomFormResponseFieldSnapshot } from "../types";
import type { CustomFormFieldPayload } from "../models/payloads";

type FieldLike = CustomFormField | CustomFormResponseFieldSnapshot | CustomFormFieldPayload;

function PreviewPlaceholder({ text, width }: { text: string; width: string }) {
  return (
    <CDS.Text14
      color="content.tertiary"
      borderBottomWidth="1px"
      borderColor="border.default"
      pb={1}
      w={width}
    >
      {text}
    </CDS.Text14>
  );
}

interface Props {
  field: FieldLike;
  mode: "submit" | "readonly" | "preview";
  value: string | string[] | number | null | undefined;
  error?: string;
  onChange?: (value: string | string[] | number | null) => void;
}

function fieldChoices(field: FieldLike): { value: string; label: string }[] {
  return field.options?.choices ?? [];
}

function formatReadonlyValue(field: FieldLike, value: Props["value"]): string {
  if (value === null || value === undefined || value === "") return "—";

  switch (field.fieldType) {
    case "single_select": {
      const choice = fieldChoices(field).find((c) => c.value === value);
      return choice?.label ?? String(value);
    }
    case "multi_select": {
      const choices = fieldChoices(field);
      const values = Array.isArray(value) ? value : [];
      if (values.length === 0) return "—";
      return values.map((v) => choices.find((c) => c.value === v)?.label ?? v).join(", ");
    }
    default:
      return Array.isArray(value) ? value.join(", ") : String(value);
  }
}

function ReadonlyValue({ field, value }: { field: FieldLike; value: Props["value"] }) {
  const text = formatReadonlyValue(field, value);
  const isLongText = field.fieldType === "long_text";
  return (
    <CDS.Text12
      whiteSpace={isLongText ? "pre-wrap" : "normal"}
      color={text === "—" ? "content.tertiary" : "content.primary"}
    >
      {text}
    </CDS.Text12>
  );
}

export default function FieldRenderer({ field, mode, value, error, onChange }: Props) {
  if (mode === "readonly") {
    return (
      <FormControl>
        <FormLabel mb={1} textStyle="textXS" color="content.tertiary" textTransform="uppercase" letterSpacing="wide">
          {field.label}
        </FormLabel>
        <ReadonlyValue field={field} value={value} />
      </FormControl>
    );
  }

  const control = (() => {
    // Preview mirrors Google Forms — scalar types get placeholders (not editable-looking inputs);
    // select types render as radios/checkboxes so all options stay visible while authoring.
    if (mode === "preview" && PREVIEW_PLACEHOLDER[field.fieldType]) {
      return (
        <PreviewPlaceholder
          text={PREVIEW_PLACEHOLDER[field.fieldType] ?? ""}
          width={PREVIEW_PLACEHOLDER_WIDTH[field.fieldType] ?? "60%"}
        />
      );
    }

    switch (field.fieldType) {
      case "short_text":
        return (
          <Input
            borderRadius="base"
            value={(value as string) ?? ""}
            onChange={(e) => onChange?.(e.target.value)}
          />
        );
      case "long_text":
        return (
          <Textarea
            borderRadius="base"
            rows={2}
            value={(value as string) ?? ""}
            onChange={(e) => onChange?.(e.target.value)}
          />
        );
      case "date":
        return (
          <Input
            type="date"
            borderRadius="base"
            value={(value as string) ?? ""}
            onChange={(e) => onChange?.(e.target.value)}
          />
        );
      case "number":
        return (
          <NumberInput
            value={value === null || value === undefined ? "" : String(value)}
            onChange={(_, num) => onChange?.(Number.isNaN(num) ? null : num)}
          >
            <NumberInputField borderRadius="base" />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        );
      case "single_select": {
        const choices = fieldChoices(field);
        if (mode === "preview") {
          return (
            <RadioGroup value={(value as string) ?? ""} onChange={(v) => onChange?.(v)}>
              <Stack spacing={2}>
                {choices.map((c) => (
                  <Radio key={c.value} value={c.value}>
                    {c.label}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
          );
        }
        return (
          <Select
            placeholder="Select..."
            borderRadius="base"
            value={(value as string) ?? ""}
            onChange={(e) => onChange?.(e.target.value)}
          >
            {choices.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
        );
      }
      case "multi_select": {
        const choices = fieldChoices(field);
        const values = Array.isArray(value) ? value : [];
        return (
          <CheckboxGroup value={values} onChange={(v) => onChange?.(v as string[])}>
            <Stack spacing={2}>
              {choices.map((c) => (
                <Checkbox key={c.value} value={c.value}>
                  {c.label}
                </Checkbox>
              ))}
            </Stack>
          </CheckboxGroup>
        );
      }
      default:
        return <CDS.Text12 color="content.negative">Unsupported field type: {field.fieldType}</CDS.Text12>;
    }
  })();

  return (
    <FormControl isInvalid={!!error}>
      <FormLabel
        mb={field.helpText ? 1 : 2}
        display="flex"
        alignItems="center"
        gap={1}
        textStyle="subtitleS"
        color="content.primary"
        requiredIndicator={<></>}
      >
        <span>{field.label}</span>
        {field.required && <CDS.Text12 as="span" color="content.negative">*</CDS.Text12>}
      </FormLabel>
      {field.helpText && (
        <FormHelperText mt={0} mb={2} textStyle="textS" color="content.tertiary">
          {field.helpText}
        </FormHelperText>
      )}
      {control}
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  );
}
